import { useEffect, useRef } from 'react';
import { useLocation, useNavigate, Link } from 'react-router-dom';
import { create } from 'zustand';
import { persist } from 'zustand/middleware';
import {
  ChevronLeft,
  ChevronRight,
  Sparkles,
  X,
  Zap,
  HelpCircle,
  ArrowRight,
  Bell,
  AlertTriangle,
  Radio,
} from 'lucide-react';
import { useQuery, useQueries } from '@tanstack/react-query';
import { cn } from '@/lib/cn';
import { xamplinApi, deriveEventStatus } from '@/lib/api/xamplin';
import { queryKeys } from '@/lib/api/queryKeys';
import { useSelectedBrandId } from '@/lib/branding/brandContextStore';
import { useT } from '@/lib/i18n/useT';
import { AssistantChat } from '@/features/ai-assistant/AssistantChat';
import { useHasFeature } from '@/lib/permissions/useCompanyFeatures';
import { PaywallOverlay } from '@/components/ui/PaywallOverlay';
import { useIsBelowDesktop } from '@/lib/hooks/useBreakpoint';
import { useFormPanel } from './formPanelStore';
import { FormPanelHost } from './FormPanelHost';
import { useJourneyPreview } from './journeyPreviewStore';

const MIN_WIDTH = 240;
const MAX_WIDTH = 560;
const DEFAULT_WIDTH = 360;
const COLLAPSED_WIDTH = 48;

/** Context-morphing panes (build-v2 G7): the rail switches between the Atledi
 * assistant, route-aware quick actions, and contextual help. */
export type RightPane = 'atledi' | 'actions' | 'alerts' | 'help';

interface RightSidebarStore {
  open: boolean;
  width: number;
  pane: RightPane;
  toggle: () => void;
  setOpen: (open: boolean) => void;
  setWidth: (w: number) => void;
  setPane: (p: RightPane) => void;
}

/**
 * Persist-key is `xotski.rightSidebar.v2` (bumped from v1 which defaulted
 * `open:true` for an activity feed). The fresh key forces existing users
 * to start with the assistant collapsed — this is intentional and matches
 * the cost-control gate: no Claude call until the user opens the panel.
 */
export const useRightSidebar = create<RightSidebarStore>()(
  persist(
    (set) => ({
      open: false,
      width: DEFAULT_WIDTH,
      pane: 'atledi',
      toggle: () => set((s) => ({ open: !s.open })),
      setOpen: (open) => set({ open }),
      setWidth: (w) =>
        set({ width: Math.max(MIN_WIDTH, Math.min(MAX_WIDTH, w)) }),
      setPane: (pane) => set({ pane }),
    }),
    { name: 'xotski.rightSidebar.v2' },
  ),
);

/** Width the AdminShell should reserve on the right for this sidebar. */
export function getRightSidebarOffset(open: boolean, width: number): number {
  return open ? width : COLLAPSED_WIDTH;
}

export function RightSidebar() {
  const t = useT();
  const open = useRightSidebar((s) => s.open);
  const width = useRightSidebar((s) => s.width);
  const toggle = useRightSidebar((s) => s.toggle);
  const setWidth = useRightSidebar((s) => s.setWidth);
  const isBelowDesktop = useIsBelowDesktop();
  // When a form is active the rail hosts that form instead of the assistant.
  const formActive = useFormPanel((s) => s.active);
  const closeForm = useFormPanel((s) => s.closeForm);
  // When the Design Journey tab is open the rail hosts its live preview
  // (portaled in from SurveyConfigSection) instead of the assistant.
  const journeyActive = useJourneyPreview((s) => s.active);
  const setJourneySlot = useJourneyPreview((s) => s.setSlot);

  const dragging = useRef(false);

  useEffect(() => {
    function onMove(e: MouseEvent) {
      if (!dragging.current) return;
      setWidth(window.innerWidth - e.clientX);
    }
    function onUp() {
      if (!dragging.current) return;
      dragging.current = false;
      document.body.style.cursor = '';
      document.body.style.userSelect = '';
    }
    document.addEventListener('mousemove', onMove);
    document.addEventListener('mouseup', onUp);
    return () => {
      document.removeEventListener('mousemove', onMove);
      document.removeEventListener('mouseup', onUp);
    };
  }, [setWidth]);

  const startDrag = (e: React.MouseEvent) => {
    e.preventDefault();
    dragging.current = true;
    document.body.style.cursor = 'col-resize';
    document.body.style.userSelect = 'none';
  };

  const renderedWidth = open ? width : COLLAPSED_WIDTH;

  // Below lg the assistant has no docked rail — it opens as a full-height
  // overlay sheet triggered from the TopBar. Only one variant is mounted at
  // a time so AssistantChat's cost-control kickoff fetch never doubles.
  if (isBelowDesktop) {
    if (!open) return null;
    return (
      <div className="fixed inset-0 z-50">
        <button
          type="button"
          aria-label={t('assistant.collapse')}
          onClick={toggle}
          className="absolute inset-0 bg-black/60 backdrop-blur-sm"
        />
        <div className="absolute inset-y-0 right-0 flex w-full max-w-sm flex-col border-l border-[var(--color-xk-border)] bg-[var(--color-xk-bg-nav)] shadow-2xl">
          <div className="flex h-14 flex-none items-center gap-2 border-b border-[var(--color-xk-border)] px-4 text-[var(--color-xk-lavender)]">
            <Sparkles className="h-4 w-4 flex-none text-[var(--color-xk-accent)]" />
            <span
              className="flex-1 text-left text-[11px] font-extrabold uppercase tracking-[0.12em]"
              style={{ fontFamily: 'var(--font-display)' }}
            >
              {formActive
                ? formActive.title
                : journeyActive
                  ? t('journeyPreview.title')
                  : t('assistant.title')}
            </span>
            <button
              type="button"
              onClick={formActive ? () => closeForm() : toggle}
              aria-label={t('assistant.collapse')}
              className="grid h-8 w-8 flex-none place-items-center rounded-md text-[var(--color-xk-text-muted)] transition-colors hover:text-[var(--color-xk-accent)]"
            >
              <X className="h-4 w-4" />
            </button>
          </div>
          <div className="flex flex-1 min-h-0 flex-col">
            {formActive ? (
              <FormPanelHost />
            ) : journeyActive ? (
              <div ref={setJourneySlot} className="flex flex-1 min-h-0 flex-col overflow-y-auto" />
            ) : (
              <ContextPanel open={open} />
            )}
          </div>
        </div>
      </div>
    );
  }

  return (
    <aside
        className="fixed inset-y-0 right-0 z-40 hidden flex-col border-l border-[var(--color-xk-border)] bg-[var(--color-xk-bg-nav)] transition-[width] duration-150 lg:flex"
        style={{ width: renderedWidth }}
      >
      {open && (
        <div
          onMouseDown={startDrag}
          aria-hidden="true"
          className="absolute inset-y-0 left-0 z-10 w-1 cursor-col-resize bg-transparent transition-colors hover:bg-[var(--color-accent-30)]"
        />
      )}

      <div
        className={cn(
          'flex h-14 flex-none items-center border-b border-[var(--color-xk-border)] text-[var(--color-xk-lavender)]',
          open ? 'gap-2 px-4' : 'justify-center',
        )}
      >
        <Sparkles className="h-4 w-4 flex-none text-[var(--color-xk-accent)]" />
        {open && (
          <span
            className="flex-1 text-left text-[11px] font-extrabold uppercase tracking-[0.12em]"
            style={{ fontFamily: 'var(--font-display)' }}
          >
            {formActive
              ? formActive.title
              : journeyActive
                ? t('journeyPreview.title')
                : t('assistant.title')}
          </span>
        )}
        {open && formActive && (
          <button
            type="button"
            onClick={() => closeForm()}
            aria-label={t('common.cancel')}
            className="grid h-8 w-8 flex-none place-items-center rounded-md text-[var(--color-xk-text-muted)] transition-colors hover:text-[var(--color-xk-accent)]"
          >
            <X className="h-4 w-4" />
          </button>
        )}
      </div>

      <button
        type="button"
        onClick={toggle}
        aria-label={open ? t('assistant.collapse') : t('assistant.expand')}
        className="absolute -left-3 top-1/2 z-50 grid h-6 w-6 -translate-y-1/2 place-items-center rounded-full border border-[var(--color-xk-border)] bg-[var(--color-xk-bg-deep)] text-[var(--color-xk-lavender)] shadow hover:text-[var(--color-xk-accent)]"
      >
        {open ? (
          <ChevronRight className="h-3.5 w-3.5" />
        ) : (
          <ChevronLeft className="h-3.5 w-3.5" />
        )}
      </button>

      {open ? (
        <div className="flex flex-1 min-h-0 flex-col">
          {formActive ? (
            <FormPanelHost />
          ) : journeyActive ? (
            <div ref={setJourneySlot} className="flex flex-1 min-h-0 flex-col overflow-y-auto" />
          ) : (
            <ContextPanel open={open} />
          )}
        </div>
      ) : (
        <div className="flex flex-1 flex-col items-center gap-3 py-4">
          <Sparkles className="h-4 w-4 text-[var(--color-xk-text-muted)]" />
        </div>
      )}
    </aside>
  );
}

/**
 * Mounts the real AssistantChat only when the company's plan includes the
 * `aiAssistant` feature. Otherwise renders a PaywallOverlay over a static
 * mock — critically, AssistantChat itself is NOT mounted on the paywall
 * path so the cost-control kickoff fetch never fires for non-purchased
 * companies (the backend's @RequiresFeature('aiAssistant') guard would
 * block it anyway, but keeping the request from leaving the browser at
 * all is cheaper and less noisy in logs).
 */
/**
 * Context-morphing rail body (build-v2 G7) — a 3-way switcher (Atledi / Actions
 * / Help) faithful to the prototype `right-rail.jsx` panes. Actions are
 * route-aware; Help links into the in-app Help Center. Atledi stays the default.
 */
function ContextPanel({ open }: { open: boolean }) {
  const pane = useRightSidebar((s) => s.pane);
  const setPane = useRightSidebar((s) => s.setPane);
  const tabs: { id: RightPane; label: string; icon: React.ReactNode }[] = [
    { id: 'atledi', label: 'Atledi', icon: <Sparkles className="h-3.5 w-3.5" /> },
    { id: 'actions', label: 'Actions', icon: <Zap className="h-3.5 w-3.5" /> },
    { id: 'alerts', label: 'Alerts', icon: <Bell className="h-3.5 w-3.5" /> },
    { id: 'help', label: 'Help', icon: <HelpCircle className="h-3.5 w-3.5" /> },
  ];
  return (
    <div className="flex flex-1 min-h-0 flex-col">
      <div className="flex flex-none gap-1 border-b border-[var(--color-xk-border)] px-2 py-1.5">
        {tabs.map((tb) => (
          <button
            key={tb.id}
            type="button"
            onClick={() => setPane(tb.id)}
            className={cn(
              'flex flex-1 items-center justify-center gap-1.5 rounded-lg px-2 py-1.5 text-[11px] font-bold transition-colors',
              pane === tb.id
                ? 'bg-[var(--color-accent-10)] text-[var(--color-xk-accent)]'
                : 'text-[var(--color-xk-text-muted)] hover:text-[var(--color-xk-lavender)]',
            )}
          >
            {tb.icon}
            {tb.label}
          </button>
        ))}
      </div>
      <div className="flex flex-1 min-h-0 flex-col">
        {pane === 'atledi' && <AssistantPanel open={open} />}
        {pane === 'actions' && <QuickActionsPane />}
        {pane === 'alerts' && <AlertsPane />}
        {pane === 'help' && <HelpPane />}
      </div>
    </div>
  );
}

/** Route-aware quick actions (prototype QuickActionsPane). */
function QuickActionsPane() {
  const navigate = useNavigate();
  const { pathname } = useLocation();
  type QA = { label: string; sub: string; to: string };
  let actions: QA[];
  if (pathname.startsWith('/insights')) {
    actions = [
      { label: 'Generate recap deck', sub: '11-slide client recap', to: '/insights' },
      { label: 'Open a market', sub: 'Drill into a project', to: '/projects' },
    ];
  } else if (pathname.startsWith('/projects')) {
    actions = [
      { label: 'Staffing & roster', sub: 'Assign BAs, spot conflicts', to: pathname.endsWith('/route') ? pathname.replace('/route', '/staffing') : `${pathname}/staffing` },
      { label: 'Route & proximity', sub: 'Same-day clusters', to: pathname.includes('/staffing') ? pathname.replace('/staffing', '/route') : `${pathname}/route` },
      { label: 'Insights', sub: 'MENT, funnel, recap', to: '/insights' },
    ];
  } else if (pathname.startsWith('/photos') || pathname.startsWith('/assets')) {
    actions = [
      { label: 'Generate recap deck', sub: 'Add approved photos', to: '/insights' },
      { label: 'My Assets', sub: 'Library by Purpose', to: '/assets' },
    ];
  } else {
    actions = [
      { label: 'New project', sub: 'Set up to measure', to: '/projects' },
      { label: 'New brand', sub: 'Another workspace', to: '/brands' },
      { label: 'Plans & pricing', sub: 'Compare tiers', to: '/pricing' },
    ];
  }
  return (
    <div className="flex flex-1 flex-col gap-2 overflow-y-auto p-3">
      <span className="mono text-[10px] uppercase tracking-wider text-[var(--color-xk-text-subtle)]">
        Quick actions
      </span>
      {actions.map((a) => (
        <button
          key={a.label}
          type="button"
          onClick={() => navigate(a.to)}
          className="flex items-center gap-2 rounded-xl border border-[var(--color-xk-border)] bg-[var(--color-xk-surface)] px-3 py-2.5 text-left transition-colors hover:border-[var(--color-xk-accent)]"
        >
          <div className="min-w-0 flex-1">
            <div className="text-xs font-semibold text-[var(--color-xk-lavender)]">{a.label}</div>
            <div className="mono text-[10px] text-[var(--color-xk-text-muted)]">{a.sub}</div>
          </div>
          <ArrowRight className="h-3.5 w-3.5 flex-none text-[var(--color-xk-text-muted)]" />
        </button>
      ))}
    </div>
  );
}

/** Alerts pane (prototype AlertsPane) — live events + open gaps derived from the
 * active brand's project structures (real data). */
function AlertsPane() {
  const selectedBrandId = useSelectedBrandId();
  const projectsQ = useQuery({ queryKey: queryKeys.xamplin.projects(), queryFn: () => xamplinApi.projects.list() });
  const projects = (projectsQ.data?.items ?? []).filter(
    (p) => !selectedBrandId || p.companyId === selectedBrandId,
  );
  const structureQs = useQueries({
    queries: projects.map((p) => ({
      queryKey: queryKeys.xamplin.projectStructure(p.id),
      queryFn: () => xamplinApi.projects.structure(p.id),
    })),
  });
  const alerts: { kind: 'live' | 'gap'; title: string; sub: string; to: string }[] = [];
  structureQs.forEach((q, i) => {
    const proj = projects[i];
    if (!proj) return;
    const groups = q.data?.structured?.project?.eventGroups ?? [];
    for (const g of groups)
      for (const e of g.events ?? []) {
        const status = deriveEventStatus(e);
        const stations = e.stations?.length ?? 0;
        const reporters = e.reporters?.length ?? 0;
        if (status === 'live') {
          alerts.push({ kind: 'live', title: `${e.name} está live`, sub: `${proj.name} · ${g.name ?? ''}`, to: `/projects/${proj.id}` });
        } else if (status !== 'completed' && (stations === 0 || reporters === 0)) {
          alerts.push({
            kind: 'gap',
            title: `${e.name}: ${stations === 0 ? 'sin stations' : 'sin reporters'}`,
            sub: `${proj.name} · ${g.name ?? ''}`,
            to: `/projects/${proj.id}`,
          });
        }
      }
  });

  return (
    <div className="flex flex-1 flex-col gap-2 overflow-y-auto p-3">
      <span className="mono text-[10px] uppercase tracking-wider text-[var(--color-xk-text-subtle)]">
        Alerts {alerts.length > 0 && `· ${alerts.length}`}
      </span>
      {alerts.length === 0 ? (
        <span className="mono text-[11px] text-[var(--color-xk-text-subtle)]">Sin alertas. 🎉</span>
      ) : (
        alerts.slice(0, 30).map((a, i) => (
          <Link
            key={i}
            to={a.to}
            className="flex items-start gap-2 rounded-xl border border-[var(--color-xk-border)] bg-[var(--color-xk-surface)] px-3 py-2 transition-colors hover:border-[var(--color-xk-accent)]"
          >
            {a.kind === 'live' ? (
              <Radio className="mt-0.5 h-3.5 w-3.5 flex-none text-[#4dd28b]" />
            ) : (
              <AlertTriangle className="mt-0.5 h-3.5 w-3.5 flex-none text-[#f4b740]" />
            )}
            <div className="min-w-0 flex-1">
              <div className="truncate text-xs font-semibold text-[var(--color-xk-lavender)]">{a.title}</div>
              <div className="mono truncate text-[10px] text-[var(--color-xk-text-muted)]">{a.sub}</div>
            </div>
          </Link>
        ))
      )}
    </div>
  );
}

/** Contextual help articles (prototype HelpPane) → in-app Help Center. */
function HelpPane() {
  const articles = [
    { id: 'overview', label: 'Setting up your first project' },
    { id: 'insights', label: 'Reading MENT scores' },
    { id: 'pricing', label: 'Exporting recap decks' },
    { id: 'route', label: 'Route & proximity planning' },
    { id: 'intake', label: 'Inviting venue stakeholders' },
    { id: 'photos', label: 'Approving photos for recap' },
  ];
  return (
    <div className="flex flex-1 flex-col gap-1.5 overflow-y-auto p-3">
      <span className="mono text-[10px] uppercase tracking-wider text-[var(--color-xk-text-subtle)]">
        Help
      </span>
      {articles.map((a) => (
        <Link
          key={a.id}
          to={`/help#${a.id}`}
          className="flex items-center gap-2 rounded-lg px-2.5 py-2 text-xs text-[var(--color-xk-text-muted)] transition-colors hover:bg-[var(--color-xk-surface)] hover:text-[var(--color-xk-lavender)]"
        >
          <HelpCircle className="h-3.5 w-3.5 flex-none text-[var(--color-xk-accent)]" />
          <span className="flex-1">{a.label}</span>
          <ArrowRight className="h-3 w-3 flex-none" />
        </Link>
      ))}
    </div>
  );
}

function AssistantPanel({ open }: { open: boolean }) {
  const t = useT();
  const hasFeature = useHasFeature('aiAssistant');

  if (!hasFeature) {
    return (
      <PaywallOverlay
        title={t('paywall.aiAssistant.title')}
        description={t('paywall.aiAssistant.description')}
        perks={[
          t('paywall.aiAssistant.perk1'),
          t('paywall.aiAssistant.perk2'),
          t('paywall.aiAssistant.perk3'),
        ]}
        className="flex flex-1"
      >
        <AssistantChatMock />
      </PaywallOverlay>
    );
  }

  return <AssistantChat open={open} />;
}

/**
 * Tiny static preview of the assistant chat used behind the paywall blur.
 * Hard-coded copy on purpose — translating mock content adds noise without
 * meaningful value, and the strings never reach interactive users.
 */
function AssistantChatMock() {
  return (
    <div className="flex flex-1 flex-col gap-3 p-4">
      <div className="rounded-lg bg-[var(--color-xk-bg-deep)] p-3 text-xs text-[var(--color-xk-text-muted)]">
        How many responses did we collect at Beach Fest yesterday?
      </div>
      <div className="rounded-lg bg-[var(--color-xk-accent-soft)] p-3 text-xs text-[var(--color-xk-text-muted)]">
        Beach Fest collected 1,284 responses across 3 stations, with the
        highest engagement between 7pm and 9pm. The top-performing station
        was Margarita Bar (520 responses, 92% completion).
      </div>
      <div className="rounded-lg bg-[var(--color-xk-bg-deep)] p-3 text-xs text-[var(--color-xk-text-muted)]">
        Which prize was redeemed most?
      </div>
      <div className="rounded-lg bg-[var(--color-xk-accent-soft)] p-3 text-xs text-[var(--color-xk-text-muted)]">
        Free Margarita — 312 wins, 89% redeemed within 30 minutes.
      </div>
    </div>
  );
}
